import React, { useState, useRef } from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

interface DoughnutSegment {
  label: string;
  value: number;
}

interface PremiumDoughnutProps {
  data: DoughnutSegment[];
  centerLabel?: string;
  centerValue?: string | number;
  height?: number;
  className?: string;
  showLegend?: boolean;
}

const PremiumDoughnut: React.FC<PremiumDoughnutProps> = ({
  data,
  centerLabel,
  centerValue,
  height = 360,
  className = '',
  showLegend = true,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: number; pct: string } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Filter out zero-value segments
  const filteredData = data.filter(d => d.value > 0);
  const total = filteredData.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return <div className="flex items-center justify-center h-full text-gray-400">No data</div>;

  // SVG viewBox — must be wide enough to fully contain leader-line labels
  // (html2canvas clips to viewBox bounds when generating PDF)
  const vbWidth = 800;
  const vbHeight = 400;
  const cx = vbWidth / 2;
  const cy = vbHeight / 2;
  const radius = 130;
  const strokeWidth = 50;
  const circumference = 2 * Math.PI * radius;

  // If one segment dominates (>90%), ensure all others get a minimum visible angle
  const minSegmentAngle = 0.08; // ~4.5 degrees minimum
  const gapAngle = filteredData.length > 1 ? 0.03 : 0; // Gap between segments

  // Calculate visual angles, ensuring minimum visibility
  let rawAngles = filteredData.map(d => (d.value / total) * 2 * Math.PI);
  const totalGap = gapAngle * filteredData.length;
  const availableAngle = 2 * Math.PI - totalGap;

  // Find segments below minimum threshold and redistribute
  const tooSmall = rawAngles.filter(a => a < minSegmentAngle);
  if (tooSmall.length > 0 && filteredData.length > 1) {
    const deficit = tooSmall.reduce((sum, a) => sum + (minSegmentAngle - a), 0);
    const largeTotal = rawAngles.filter(a => a >= minSegmentAngle).reduce((s, a) => s + a, 0);

    rawAngles = rawAngles.map(a => {
      if (a < minSegmentAngle) return minSegmentAngle;
      // Proportionally shrink larger segments to make room
      return a - (deficit * (a / largeTotal));
    });
  }

  // Build segments with their visual properties
  const segments = filteredData.map((d, i) => ({
    ...d,
    pct: d.value / total,
    visualAngle: rawAngles[i],
    color: CHART_PALETTE[i % CHART_PALETTE.length],
    originalIndex: i,
  }));

  // Build arc geometry
  let cumulativeAngle = -Math.PI / 2; // Start at top

  const arcs = segments.map((seg, i) => {
    const segmentAngle = seg.visualAngle;
    const midAngle = cumulativeAngle + segmentAngle / 2;

    // Dasharray for this segment
    const dashLength = Math.max((segmentAngle - gapAngle) * radius, 1);
    const dashGap = circumference - dashLength;
    const offset = -(cumulativeAngle + gapAngle / 2) * radius;

    // Leader line endpoints — kept compact so labels stay within viewBox
    const leaderRadius = radius + strokeWidth / 2 + 6;
    const elbowRadius = leaderRadius + 16;
    const leaderStart = {
      x: cx + Math.cos(midAngle) * leaderRadius,
      y: cy + Math.sin(midAngle) * leaderRadius,
    };
    const leaderElbow = {
      x: cx + Math.cos(midAngle) * elbowRadius,
      y: cy + Math.sin(midAngle) * elbowRadius,
    };
    const leaderEnd = {
      x: leaderElbow.x + (Math.cos(midAngle) > 0 ? 18 : -18),
      y: leaderElbow.y,
    };
    const textAnchor = Math.cos(midAngle) > 0 ? 'start' : 'end';
    const textX = leaderEnd.x + (Math.cos(midAngle) > 0 ? 4 : -4);

    const result = {
      ...seg,
      index: i,
      dashLength,
      dashGap,
      offset,
      midAngle,
      leaderStart,
      leaderElbow,
      leaderEnd,
      textAnchor,
      textX,
    };

    cumulativeAngle += segmentAngle;
    return result;
  });

  const handleMouseMove = (e: React.MouseEvent, arc: typeof arcs[0]) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 12,
      label: arc.label,
      value: arc.value,
      pct: (arc.pct * 100).toFixed(1),
    });
    setHoveredIndex(arc.index);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
    setHoveredIndex(null);
  };

  const displayValue = centerValue ?? total;

  // Show leader lines for any segment with > 1.5% (lowered threshold)
  const leaderThreshold = 0.015;

  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      <div className="flex items-center justify-center w-full" style={{ height: Math.max(height - 50, 260) }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${vbWidth} ${vbHeight}`}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Gradient definitions */}
          <defs>
            {arcs.map((arc) => (
              <linearGradient key={`doughnut-grad-${arc.index}`} id={`doughnut-grad-${arc.index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={arc.color.gradient[0]} />
                <stop offset="100%" stopColor={arc.color.gradient[1]} />
              </linearGradient>
            ))}
          </defs>

          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={SURFACE.borderLight}
            strokeWidth={strokeWidth}
          />

          {/* Segments */}
          {arcs.map((arc) => (
            <circle
              key={arc.index}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={`url(#doughnut-grad-${arc.index})`}
              strokeWidth={hoveredIndex === arc.index ? strokeWidth + 6 : strokeWidth}
              strokeDasharray={`${arc.dashLength} ${arc.dashGap}`}
              strokeDashoffset={arc.offset}
              strokeLinecap="butt"
              style={{
                transition: 'stroke-width 0.2s ease, opacity 0.2s ease',
                opacity: hoveredIndex !== null && hoveredIndex !== arc.index ? 0.45 : 1,
                cursor: 'pointer',
              }}
              onMouseMove={(e) => handleMouseMove(e, arc)}
              onMouseLeave={handleMouseLeave}
            />
          ))}

          {/* Leader lines and labels */}
          {arcs.filter(a => a.pct >= leaderThreshold).map((arc) => (
            <g key={`label-${arc.index}`} style={{ opacity: hoveredIndex !== null && hoveredIndex !== arc.index ? 0.25 : 1, transition: 'opacity 0.2s ease' }}>
              <polyline
                points={`${arc.leaderStart.x},${arc.leaderStart.y} ${arc.leaderElbow.x},${arc.leaderElbow.y} ${arc.leaderEnd.x},${arc.leaderEnd.y}`}
                fill="none"
                stroke={SURFACE.textMuted}
                strokeWidth={1.2}
              />
              <text
                x={arc.textX}
                y={arc.leaderEnd.y - 4}
                textAnchor={arc.textAnchor as any}
                fill={SURFACE.text}
                fontSize={13}
                fontWeight={600}
              >
                {arc.label.length > 22 ? arc.label.substring(0, 20) + '..' : arc.label}
              </text>
              <text
                x={arc.textX}
                y={arc.leaderEnd.y + 12}
                textAnchor={arc.textAnchor as any}
                fill={SURFACE.textSecondary}
                fontSize={11}
              >
                {arc.value.toLocaleString()} ({(arc.pct * 100).toFixed(1)}%)
              </text>
            </g>
          ))}

          {/* Center text */}
          <text
            x={cx}
            y={cy - 8}
            textAnchor="middle"
            fill={SURFACE.text}
            fontSize={38}
            fontWeight={700}
            fontFamily="system-ui, -apple-system, sans-serif"
          >
            {typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}
          </text>
          {centerLabel && (
            <text
              x={cx}
              y={cy + 18}
              textAnchor="middle"
              fill={SURFACE.textMuted}
              fontSize={15}
              fontWeight={500}
            >
              {centerLabel}
            </text>
          )}
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-10"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap">
            <div className="font-semibold">{tooltip.label}</div>
            <div className="text-gray-300 mt-0.5">{tooltip.value.toLocaleString()} ({tooltip.pct}%)</div>
          </div>
        </div>
      )}

      {/* Legend below chart */}
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 px-2 py-2">
          {segments.map((seg, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 cursor-pointer py-0.5"
              style={{
                opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.4 : 1,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${seg.color.gradient[0]}, ${seg.color.gradient[1]})` }}
              />
              <span className="text-xs text-gray-600 truncate" style={{ maxWidth: 130 }}>
                {seg.label}
              </span>
              <span className="text-xs text-gray-400">
                {(seg.pct * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PremiumDoughnut;
